// PHA-1168: the one renderer for a custom view -- the builder preview, the
// Patient List and the Billing Report all draw through it, so a view looks the
// same where it is designed and where it is used.
import React from 'react';
import { Badge, Flex, Table, Text } from '@radix-ui/themes';
import { ArrowDownIcon, ArrowUpIcon } from '@radix-ui/react-icons';
import type { CustomViewManifest, CustomViewResult, CustomViewRow } from '../../services/api';
import { formatCell, resolveRowStyle, STYLE_COLORS } from './format';

export interface ManifestTableSort {
  field: string;
  direction: 'asc' | 'desc';
}

interface ManifestTableProps {
  manifest: CustomViewManifest;
  records: CustomViewRow[];
  /** Billing views: per-group counts and subtotals, shown after each group. */
  groups?: CustomViewResult['groups'];
  totals?: Record<string, number>;
  sort?: ManifestTableSort | null;
  onSort?: (sort: ManifestTableSort | null) => void;
  onRowClick?: (row: CustomViewRow) => void;
  emptyText?: string;
  dense?: boolean;
}

const ManifestTable: React.FC<ManifestTableProps> = ({
  manifest, records, groups, totals, sort, onSort, onRowClick, emptyText, dense,
}) => {
  const columns = manifest.columns;
  const groupInfo = new Map((groups || []).map((g) => [g.key ?? null, g]));
  const subtotalKeys = new Set(manifest.billing?.subtotals || []);

  const headerClick = (key: string, sortable: boolean) => {
    if (!onSort || !sortable) return;
    if (!sort || sort.field !== key) onSort({ field: key, direction: 'asc' });
    else if (sort.direction === 'asc') onSort({ field: key, direction: 'desc' });
    else onSort(null); // third click: back to the view's own order
  };

  const subtotalRow = (key: string | null, label: string, values: Record<string, number>) => (
    <Table.Row key={`subtotal-${label}-${key}`} style={{ background: '#f8f9fa' }}>
      {columns.map((c, i) => (
        <Table.Cell key={c.key}>
          <Text size="1" weight="bold">
            {c.key in values ? formatCell(values[c.key], c.type) : i === 0 ? label : ''}
          </Text>
        </Table.Cell>
      ))}
    </Table.Row>
  );

  const body: React.ReactNode[] = [];
  records.forEach((row, index) => {
    const style = resolveRowStyle(row, manifest);
    body.push(
      <Table.Row
        key={`${row.patient_id}-${index}`}
        data-testid="manifest-row"
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        style={{ background: style.rowBg, cursor: onRowClick ? 'pointer' : undefined }}
        className={onRowClick ? 'hover:bg-slate-50' : undefined}
      >
        {columns.map((c, ci) => {
          const badges = style.badges[c.key] || [];
          const rowBadges = ci === 0 ? style.rowBadges : [];
          const value = formatCell(row[c.key], c.type);
          return (
            <Table.Cell
              key={c.key}
              style={{ background: style.cellBg[c.key], verticalAlign: 'middle' }}
            >
              <Flex align="center" gap="1" wrap="wrap">
                <Text size={dense ? '1' : '2'} title={c.type === 'text_list' ? value : undefined}
                  className={c.type === 'text_list' ? 'line-clamp-2' : undefined}>
                  {value}
                </Text>
                {[...rowBadges, ...badges].map((b, bi) => (
                  <Badge key={bi} size="1" color={STYLE_COLORS[b.color]?.radix || 'gray'} variant="soft">
                    {b.text}
                  </Badge>
                ))}
              </Flex>
            </Table.Cell>
          );
        })}
      </Table.Row>,
    );
    const next = records[index + 1];
    const group = row._group ?? null;
    if (groups && groups.length && (!next || (next._group ?? null) !== group)) {
      const g = groupInfo.get(group);
      if (g && Object.keys(g.subtotals || {}).length) {
        body.push(subtotalRow(group, `Subtotal: ${group ?? '(blank)'} (${g.count})`, g.subtotals));
      }
    }
  });

  return (
    <Table.Root size="1" variant="surface" data-testid="manifest-table">
      <Table.Header>
        <Table.Row className="bg-slate-50">
          {columns.map((c) => {
            const active = sort && sort.field === c.key;
            const clickable = !!onSort && c.sortable;
            return (
              <Table.ColumnHeaderCell
                key={c.key}
                onClick={() => headerClick(c.key, c.sortable)}
                style={{ cursor: clickable ? 'pointer' : undefined, whiteSpace: 'nowrap' }}
                aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                <Flex align="center" gap="1">
                  {c.calculated && <Text size="1" color="gray" title="Calculated field">ƒ</Text>}
                  <span>{c.label}</span>
                  {active && (sort!.direction === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />)}
                </Flex>
              </Table.ColumnHeaderCell>
            );
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {records.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={Math.max(1, columns.length)}>
              <Text size="2" color="gray">{emptyText || 'No patients match this view.'}</Text>
            </Table.Cell>
          </Table.Row>
        ) : body}
        {totals && Object.keys(totals).length > 0 && subtotalKeys.size > 0 &&
          subtotalRow(null, 'Total', totals)}
      </Table.Body>
    </Table.Root>
  );
};

export default ManifestTable;
